/* Common Imports */
import { Box, styled } from "@mui/system";
import React from "react";

/* Component Imports */
import {
	Alert,
	FormControl,
	FormControlLabel,
	FormHelperText,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Snackbar,
	SwipeableDrawer,
	TextField,
	Typography,
	keyframes,
} from "@mui/material";

import { Close } from "@mui/icons-material";
import { NextRouter } from "next/router";
import BeegruButton from "../common-components/buttons/BeegruButton";

/* Theme tokens */
const BG = "#0d0520";
const BG_INPUT = "rgba(255, 255, 255, 0.04)";
const BORDER = "rgba(180, 120, 255, 0.3)";
const BORDER_HOVER = "rgba(180, 120, 255, 0.6)";
const BORDER_FOCUS = "rgba(180, 120, 255, 0.9)";
const TEXT = "#EEF2FF";
const TEXT_DIM = "rgba(238, 242, 255, 0.6)";
const DIVIDER = "rgba(180, 120, 255, 0.15)";
const PURPLE = "#9b5cff";
const DROPDOWN_BG = "#14082a";

const glowPulse = keyframes`
	0%, 100% {
		box-shadow: 0 0 10px rgba(155, 92, 255, 0.35), 0 0 20px rgba(155, 92, 255, 0.18), inset 0 0 8px rgba(155, 92, 255, 0.18);
	}
	50% {
		box-shadow: 0 0 16px rgba(170, 110, 255, 0.6), 0 0 32px rgba(155, 92, 255, 0.32), inset 0 0 10px rgba(170, 110, 255, 0.26);
	}
`;

/* Styled Components */
const DrawerHeader = styled(Box)(() => ({
	padding: "16px 24px",
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	borderBottom: `1px solid ${DIVIDER}`,
	backgroundColor: BG,
	position: "sticky",
	top: 0,
	zIndex: 1,
}));

const DrawerContent = styled(Box)(() => ({
	padding: "1rem 1rem",
	display: "flex",
	flexDirection: "column",
	gap: "0.25rem",
	backgroundColor: BG,
	overflowY: "auto",
	flex: 1,
}));

const StyledBeegruButton = styled(BeegruButton)`
	&& {
		&.MuiButton-outlined {
			background: linear-gradient(135deg, rgba(8,2,20,0.95) 0%, rgba(28,9,58,0.9) 100%);
			color: ${TEXT};
			border-color: rgba(180, 120, 255, 0.6);
			border-radius: 0.5rem;
			padding: 0.875rem 2rem;
			font-size: 1rem;
			font-weight: 600;
			backdrop-filter: blur(14px) saturate(180%);
			animation: ${glowPulse} 3.2s ease-in-out infinite;
			transition: border-color 0.25s ease;
			width: 100%;
			margin-top: 1rem;
		}
		&.MuiButton-outlined:hover {
			border-color: rgba(200, 150, 255, 0.9);
			background: linear-gradient(135deg, rgba(14,4,32,0.97) 0%, rgba(42,14,84,0.92) 100%);
			animation: none;
			box-shadow: 0 0 16px rgba(170, 110, 255, 0.7), 0 0 36px rgba(155, 92, 255, 0.4), inset 0 0 10px rgba(170, 110, 255, 0.3);
		}
	}
`;

const DARK_MENU_PROPS = (locale: string) => ({
	PaperProps: {
		sx: {
			backgroundColor: DROPDOWN_BG,
			border: `1px solid ${DIVIDER}`,
			direction: locale === "ar-AE" ? "rtl" : "ltr",
			"& .MuiMenuItem-root": {
				color: TEXT,
				"&:hover": { backgroundColor: "rgba(180, 120, 255, 0.12)" },
				"&.Mui-selected": {
					backgroundColor: "rgba(155, 92, 255, 0.18)",
					"&:hover": { backgroundColor: "rgba(180, 120, 255, 0.22)" },
				},
			},
		},
	},
});

const StyledSelect = styled(Select)(() => ({
	color: TEXT,
	backgroundColor: BG_INPUT,
	"& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
	"&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
	"& .MuiSelect-icon": { color: TEXT_DIM },
	"& .MuiSelect-select:focus": { backgroundColor: "transparent !important" },
	"&.Mui-focused": { backgroundColor: "transparent !important" },
	flexShrink: 0,
}));

const StyledTextField = styled(TextField)(() => ({
	"& .MuiOutlinedInput-root": {
		color: TEXT,
		backgroundColor: BG_INPUT,
		"& fieldset": { borderColor: BORDER },
		"&:hover fieldset": { borderColor: BORDER_HOVER },
		"&.Mui-focused fieldset": { borderColor: `${BORDER_FOCUS} !important`, borderWidth: "1px" },
	},
	"& .MuiOutlinedInput-notchedOutline": { transition: "none" },
	"& .MuiFormLabel-root": {
		color: TEXT_DIM,
		"&.Mui-focused": { color: PURPLE },
		"&.Mui-required .MuiFormLabel-asterisk": { color: "#ff6b9d", display: "inline-block", visibility: "visible" },
	},
	"& .MuiFormLabel-asterisk": { color: "#ff6b9d" },
	"& .MuiFormHelperText-root": { color: "#ff6b9d" },
	"& input": { color: TEXT },
}));

const BoostContactFormModal = ({
	open,
	handleClose,
	dispatch,
	router,
}: {
	open: boolean;
	handleClose: () => void;
	dispatch: Function;
	router: NextRouter;
}) => {
	/* Translations */

	const localLang = {
		lets_talk: router.locale === "ar-AE" ? "هيا نتحدث" : router.locale === "kn-IN" ? "ನಾವು ಮಾತನಾಡೋಣ" : "Let's Talk",
		full_name: router.locale === "ar-AE" ? "الاسم الكامل" : router.locale === "kn-IN" ? "ಪೂರ್ಣ ಹೆಸರು" : "Full name",
		country_code: router.locale === "ar-AE" ? "رمز الدولة" : router.locale === "kn-IN" ? "ದೇಶ ಕೋಡ್" : "Country Code",
		enter_your_phone_number:
			router.locale === "ar-AE"
				? "أدخل رقم هاتفك"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಫೋನ್ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ"
					: "Enter your phone number",
		phone_number:
			router.locale === "ar-AE" ? "رقم الهاتف" : router.locale === "kn-IN" ? "ದೂರವಾಣಿ ಸಂಖ್ಯೆ" : "Phone number",
		time_preference:
			router.locale === "ar-AE" ? "تفضيل الوقت" : router.locale === "kn-IN" ? "ಸಮಯ ಪ್ರಾಧಾನ್ಯತೆ" : "Time preference",
		pick_time_slot:
			router.locale === "ar-AE"
				? "اختر فترة زمنية"
				: router.locale === "kn-IN"
					? "ಸಮಯ ಸ್ಲಾಟ್ ಆಯ್ಕೆಮಾಡಿ"
					: "Pick time slot",
		anytime: router.locale === "ar-AE" ? "في أي وقت" : router.locale === "kn-IN" ? "ಯಾವುದೇ ಸಮಯದಲ್ಲಿ" : "Anytime",
		morning:
			router.locale === "ar-AE"
				? "الصباح (8 صباحًا - 12 مساءً)"
				: router.locale === "kn-IN"
					? "ಬೆಳಿಗ್ಗೆ (8am - 12pm)"
					: "Morning (8am - 12pm)",
		afternoon:
			router.locale === "ar-AE"
				? "بعد الظهر (12 مساءً - 4 مساءً)"
				: router.locale === "kn-IN"
					? "ಮಧ್ಯಾಹ್ನ (12pm - 4pm)"
					: "Afternoon (12pm - 4pm)",
		evening:
			router.locale === "ar-AE"
				? "المساء (4 مساءً - 8 مساءً)"
				: router.locale === "kn-IN"
					? "ಸಂಜೆ (4pm - 8pm)"
					: "Evening (4pm - 8pm)",
		weekends: router.locale === "ar-AE" ? "عطلة نهاية الأسبوع" : router.locale === "kn-IN" ? "ವಾರಾಂತ್ಯ" : "Weekend",
		i_am_a: router.locale === "ar-AE" ? "أنا" : router.locale === "kn-IN" ? "ನಾನು" : "I am a",
		agent: router.locale === "ar-AE" ? "وكيل" : router.locale === "kn-IN" ? "ಏಜೆಂಟ್" : "Agent",
		owner: router.locale === "ar-AE" ? "مالك" : router.locale === "kn-IN" ? "ಮಾಲೀಕ" : "Owner",
		developer: router.locale === "ar-AE" ? "مطور" : router.locale === "kn-IN" ? "ವಿಕಸಕ" : "Developer",
		request_callback:
			router.locale === "ar-AE"
				? "طلب معاودة الاتصال"
				: router.locale === "kn-IN"
					? "ಕಾಲ್‌ಬ್ಯಾಕ್ ವಿನಂತಿಸಿ"
					: "Request a callback",
		youre_one_step_closer:
			router.locale === "ar-AE"
				? "أنت على بعد خطوة واحدة من البيع! سيتصل بك فريقنا قريبًا."
				: router.locale === "kn-IN"
					? "ನೀವು ಮಾರಾಟದ ಒಂದು ಹಂತದ ದೂರದಲ್ಲಿದ್ದೀರಿ! ನಮ್ಮ ತಂಡವು ಶೀಘ್ರದಲ್ಲೇ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತದೆ."
					: "You're one step closer to selling! Our team will contact you shortly.",
		submission_error:
			router.locale === "ar-AE"
				? "حدث خطأ. يرجى المحاولة مرة أخرى."
				: router.locale === "kn-IN"
					? "ದೋಷ ಸಂಭವಿಸಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."
					: "Something went wrong. Please try again.",
		full_name_required:
			router.locale === "ar-AE"
				? "الاسم الكامل مطلوب"
				: router.locale === "kn-IN"
					? "ಪೂರ್ಣ ಹೆಸರು ಅಗತ್ಯವಿದೆ"
					: "Full name is required",
		phone_number_required:
			router.locale === "ar-AE"
				? "رقم الهاتف مطلوب"
				: router.locale === "kn-IN"
					? "ದೂರವಾಣಿ ಸಂಖ್ಯೆ ಅಗತ್ಯವಿದೆ"
					: "Phone number is required",
		phone_number_digits:
			router.locale === "ar-AE"
				? "يجب أن يكون رقم الهاتف مكونًا من 10 أرقام"
				: router.locale === "kn-IN"
					? "ದೂರವಾಣಿ ಸಂಖ್ಯೆ 10 ಅಂಕಿಗಳಾಗಿರಬೇಕು"
					: "Phone number must be 10 digits",
		time_slot_required:
			router.locale === "ar-AE"
				? "يرجى اختيار فترة زمنية"
				: router.locale === "kn-IN"
					? "ದಯವಿಟ್ಟು ಸಮಯ ಸ್ಲಾಟ್ ಆಯ್ಕೆಮಾಡಿ"
					: "Please select a time slot",
	};

	const [name, setName] = React.useState<string>("");

	const [phone, setPhone] = React.useState<string>("");

	const [countryCode, setCountryCode] = React.useState<string>("+91");

	const [budget, setBudget] = React.useState<number>(0);

	const [timepreference, setTimePreference] = React.useState<string>("*");

	const [showSuccessSnackbar, setShowSuccessSnackbar] = React.useState(false);
	const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
	const [submitting, setSubmitting] = React.useState(false);

	const [userType, setUserType] = React.useState<string>("agent");

	const [formErrors, setFormErrors] = React.useState<{
		name: string;
		phone: string;
		timepreference: string;
	}>({
		name: "",
		phone: "",
		timepreference: "",
	});

	const resetForm = () => {
		setName("");
		setPhone("");
		setCountryCode("+91");
		setBudget(0);
		setTimePreference("*");
		setFormErrors({ name: "", phone: "", timepreference: "" });
		setUserType("agent");
	};

	const submit = async () => {
		setFormErrors({ name: "", phone: "", timepreference: "" });
		let hasError = false;
		const newErrors = { name: "", phone: "", timepreference: "" };

		if (!name.trim()) {
			newErrors.name = localLang.full_name_required;
			hasError = true;
		}

		if (!phone) {
			newErrors.phone = localLang.phone_number_required;
			hasError = true;
		} else if (phone.length !== 10) {
			newErrors.phone = localLang.phone_number_digits;
			hasError = true;
		}

		if (timepreference === "*") {
			newErrors.timepreference = localLang.time_slot_required;
			hasError = true;
		}

		if (hasError) {
			setFormErrors(newErrors);
			return;
		}

		setSubmitting(true);
		try {
			// @ts-ignore
			const recaptchaToken: string = await grecaptcha.enterprise.execute(
				process.env.RECAPTCHA_SITE_KEY_PRODUCTION || "",
				{ action: "LOGIN" },
			);
			const res = await fetch(
				process.env.PRODUCTION_API_URL + "business-dashboard/ai-boost/store-aiboosts-b2b-leads",
				{
					method: "POST",
					headers: { "Content-Type": "text/plain" },
					body: JSON.stringify({
						name,
						phone: { phone_number: phone, country_code: countryCode },
						notes: `${localLang.i_am_a} ${userType}`,
						budget,
						contact_preferences: timepreference,
						source: { url: "", component: "", form_type: "aiboost_landingpage_mobile" },
						recaptcha_token: recaptchaToken,
					}),
				},
			);
			const data = await res.json();
			if (data.status === "success" || data.status === "exists") {
				setShowSuccessSnackbar(true);
				resetForm();
				handleClose();
			} else {
				setShowErrorSnackbar(true);
			}
		} catch {
			setShowErrorSnackbar(true);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<Snackbar
				open={showSuccessSnackbar}
				autoHideDuration={5000}
				onClose={() => setShowSuccessSnackbar(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setShowSuccessSnackbar(false)}
					severity="success"
					sx={{
						backgroundColor: "#14082a",
						color: TEXT,
						border: "1px solid rgba(100, 220, 120, 0.35)",
						direction: router.locale === "ar-AE" ? "rtl" : "ltr",
						"& .MuiAlert-icon": { color: "#6dffaa" },
						"& .MuiAlert-action .MuiIconButton-root": { color: TEXT_DIM },
					}}
				>
					{localLang.youre_one_step_closer}
				</Alert>
			</Snackbar>

			<Snackbar
				open={showErrorSnackbar}
				autoHideDuration={6000}
				onClose={() => setShowErrorSnackbar(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setShowErrorSnackbar(false)}
					severity="error"
					sx={{
						backgroundColor: "#14082a",
						color: TEXT,
						border: "1px solid rgba(255, 100, 100, 0.35)",
						direction: router.locale === "ar-AE" ? "rtl" : "ltr",
						"& .MuiAlert-icon": { color: "#ff6b9d" },
						"& .MuiAlert-action .MuiIconButton-root": { color: TEXT_DIM },
					}}
				>
					{localLang.submission_error}
				</Alert>
			</Snackbar>

			<SwipeableDrawer
				anchor="bottom"
				open={open}
				onClose={() => { handleClose(); resetForm(); }}
				onOpen={() => {}}
				PaperProps={{
					sx: {
						height: "auto",
						maxHeight: "90vh",
						backgroundColor: BG,
						borderTopLeftRadius: "1.25rem",
						borderTopRightRadius: "1.25rem",
						borderTop: `1px solid ${DIVIDER}`,
						overflowY: "auto",
						direction: router.locale === "ar-AE" ? "rtl" : "ltr",
					},
				}}
			>
				<DrawerHeader>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography variant="h6" sx={{ color: TEXT, fontWeight: 600 }}>{localLang.lets_talk}</Typography>
					</Box>
					<Box
						onClick={() => { handleClose(); resetForm(); }}
						sx={{ cursor: "pointer" }}
					>
						<Close sx={{ color: TEXT_DIM, "&:hover": { color: TEXT } }} />
					</Box>
				</DrawerHeader>

				<DrawerContent>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: "1.5rem",
							pb: 10,
							paddingTop: "0.5rem",
						}}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: "1.5rem",
							}}
						>
							<StyledTextField
								required
								fullWidth
								label={localLang.full_name}
								variant="outlined"
								value={name}
								autoComplete="off"
								InputLabelProps={{
									style: {
										textAlign: router.locale === "ar-AE" ? "right" : "left",
										width: "100%",
									},
								}}
								onChange={(e) => {
									setName(e.target.value);
									if (formErrors.name) {
										setFormErrors((prev) => ({ ...prev, name: "" }));
									}
								}}
								error={!!formErrors.name}
								helperText={formErrors.name}
							/>
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									gap: "0.5rem",
									alignItems: "flex-start",
								}}
							>
								<StyledSelect
									value={countryCode}
									onChange={(e) => setCountryCode(e.target.value as string)}
									variant="outlined"
									displayEmpty
									inputProps={{ "aria-label": localLang.country_code }}
									MenuProps={DARK_MENU_PROPS(router.locale || "en-IN")}
								>
									<MenuItem value="+91" sx={{ color: TEXT }}>+91</MenuItem>
									<MenuItem value="+1" sx={{ color: TEXT }}>+1</MenuItem>
									<MenuItem value="+971" sx={{ color: TEXT }}>+971</MenuItem>
								</StyledSelect>
								<StyledTextField
									fullWidth
									placeholder={localLang.enter_your_phone_number}
									variant="outlined"
									label={localLang.phone_number}
									required
									value={phone}
									autoComplete="off"
									onChange={(e) => {
										const value = e.target.value.replace(/\D/g, "");
										if (value.length <= 10) {
											setPhone(value);
											if (formErrors.phone) {
												setFormErrors((prev) => ({ ...prev, phone: "" }));
											}
										}
									}}
									InputLabelProps={{
										style: {
											textAlign: router.locale === "ar-AE" ? "right" : "left",
											width: "100%",
										},
									}}
									inputProps={{
										maxLength: 10,
										pattern: "[0-9]*",
										inputMode: "numeric",
									}}
									error={!!formErrors.phone}
									helperText={formErrors.phone}
								/>
							</Box>
							<FormControl
								required
								fullWidth
								error={!!formErrors.timepreference}
								variant="outlined"
							>
								<StyledSelect
									value={timepreference}
									onChange={(e) => {
										setTimePreference(e.target.value as string);
										if (formErrors.timepreference) {
											setFormErrors((prev) => ({ ...prev, timepreference: "" }));
										}
									}}
									displayEmpty
									inputProps={{ "aria-label": localLang.pick_time_slot }}
									MenuProps={DARK_MENU_PROPS(router.locale || "en-IN")}
								>
									<MenuItem value="*" disabled sx={{ color: TEXT_DIM }}>
										<em style={{ fontStyle: "normal" }}>{localLang.pick_time_slot}</em>
									</MenuItem>
									<MenuItem value="anytime" sx={{ color: TEXT }}>{localLang.anytime}</MenuItem>
									<MenuItem value="morning" sx={{ color: TEXT }}>{localLang.morning}</MenuItem>
									<MenuItem value="afternoon" sx={{ color: TEXT }}>{localLang.afternoon}</MenuItem>
									<MenuItem value="evening" sx={{ color: TEXT }}>{localLang.evening}</MenuItem>
									<MenuItem value="weekends" sx={{ color: TEXT }}>{localLang.weekends}</MenuItem>
								</StyledSelect>
								{formErrors.timepreference && (
									<FormHelperText sx={{ color: "#ff6b9d" }}>{formErrors.timepreference}</FormHelperText>
								)}
							</FormControl>
							<Box sx={{ mt: 1 }}>
								<Typography
									variant="subtitle2"
									sx={{ mb: 1, color: TEXT_DIM }}
								>
									{localLang.i_am_a}
								</Typography>
								<RadioGroup
									row
									value={userType}
									onChange={(e) => setUserType(e.target.value)}
								>
									<FormControlLabel
										value="agent"
										control={
											<Radio
												sx={{
													color: BORDER,
													"&.Mui-checked": { color: PURPLE },
												}}
											/>
										}
										label={<Typography sx={{ color: TEXT, fontSize: "0.9rem" }}>{localLang.agent}</Typography>}
									/>
									<FormControlLabel
										value="owner"
										control={
											<Radio
												sx={{
													color: BORDER,
													"&.Mui-checked": { color: PURPLE },
												}}
											/>
										}
										label={<Typography sx={{ color: TEXT, fontSize: "0.9rem" }}>{localLang.owner}</Typography>}
									/>
									<FormControlLabel
										value="developer"
										control={
											<Radio
												sx={{
													color: BORDER,
													"&.Mui-checked": { color: PURPLE },
												}}
											/>
										}
										label={<Typography sx={{ color: TEXT, fontSize: "0.9rem" }}>{localLang.developer}</Typography>}
									/>
								</RadioGroup>
							</Box>
						</Box>
						<StyledBeegruButton
							variant="outlined"
							flavor="boost"
							onClick={submit}
							disabled={submitting}
						>
							{submitting ? "..." : localLang.request_callback}
						</StyledBeegruButton>
					</Box>
				</DrawerContent>
			</SwipeableDrawer>
		</>
	);
};

export default BoostContactFormModal;
